import { useState } from "react";
import {
  useAgentRunsChart,
  useAgentRuns,
  useAgentQueries,
  useCreateAgentQuery,
} from "@/hooks/use-queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Bot,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  DollarSign,
  MessageSquare,
  Send,
  RefreshCw,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

// ─── Stat Card ───────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon,
  iconColor,
  label,
  value,
  sub,
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card className="glass-panel">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 ${iconColor}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">
              {label}
            </p>
            <p className="text-2xl font-display font-bold truncate">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  if (status === "succeeded")
    return <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20">succeeded</Badge>;
  if (status === "failed")
    return <Badge className="bg-red-500/15 text-red-400 border-red-500/20">failed</Badge>;
  return <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20">{status}</Badge>;
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel rounded-lg px-3 py-2 text-xs space-y-1">
      <p className="font-mono text-muted-foreground">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {typeof p.value === "number" && p.name === "cost ($)" ? `$${p.value.toFixed(4)}` : p.value}
        </p>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OpenClawPage() {
  const [days, setDays] = useState(30);
  const [question, setQuestion] = useState("");
  const queryClient = useQueryClient();

  const { data: chart, isLoading: chartLoading } = useAgentRunsChart(days);
  const { data: runs, isLoading: runsLoading } = useAgentRuns(days);
  const { data: queries, isLoading: queriesLoading } = useAgentQueries();
  const createQuery = useCreateAgentQuery();

  const totals = chart?.totals ?? {};
  const daily = chart?.daily ?? [];

  const handleAsk = () => {
    if (!question.trim()) return;
    createQuery.mutate(question.trim(), { onSuccess: () => setQuestion("") });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-mono text-primary uppercase tracking-[0.2em] mb-1">Agent</h2>
          <h1 className="text-3xl font-display font-bold flex items-center gap-2">
            <Bot className="h-7 w-7 text-primary" /> OpenClaw
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Autonomous agent activity — Moltbook engagement, token spend, and query interface.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 14, 30].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? "default" : "outline"}
              onClick={() => setDays(d)}
              className="font-mono text-xs"
            >
              {d}d
            </Button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => queryClient.invalidateQueries({ queryKey: ["agent-runs-chart"] })}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {chartLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="glass-panel">
              <CardContent className="pt-5 pb-4">
                <Skeleton className="h-14 w-full" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <StatCard
              icon={CheckCircle2}
              iconColor="text-emerald-400"
              label="Runs"
              value={totals.runs ?? 0}
              sub={`${totals.succeeded ?? 0} succeeded`}
            />
            <StatCard
              icon={Zap}
              iconColor="text-blue-400"
              label="Actions"
              value={totals.actions ?? 0}
              sub="comments / posts / replies"
            />
            <StatCard
              icon={DollarSign}
              iconColor="text-amber-400"
              label="Cost"
              value={`$${(totals.costUsd ?? 0).toFixed(2)}`}
              sub={`last ${days}d (OpenRouter)`}
            />
            <StatCard
              icon={MessageSquare}
              iconColor="text-purple-400"
              label="Queries"
              value={queries?.filter((q: any) => q.status === "pending").length ?? 0}
              sub="pending answers"
            />
          </>
        )}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Actions over time */}
        <Card className="glass-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-blue-400">
              Actions over time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : daily.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">No data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={daily} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="actionsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.5)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(v) => v.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="actions"
                    name="actions"
                    stroke="#3b82f6"
                    fill="url(#actionsGrad)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="runs"
                    name="runs"
                    stroke="#8b5cf6"
                    fill="none"
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Daily cost */}
        <Card className="glass-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-wider text-amber-400">
              Daily LLM cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : daily.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">No data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={daily} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.5)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(v) => v.slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(v) => `$${v.toFixed(2)}`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="costUsd" name="cost ($)" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Query interface */}
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-purple-400 flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Ask OpenClaw
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Textarea
              placeholder="Ask OpenClaw anything — it will answer on its next scheduled run..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="resize-none text-sm"
              rows={2}
            />
            <Button
              onClick={handleAsk}
              disabled={!question.trim() || createQuery.isPending}
              className="shrink-0 self-end"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>

          {queriesLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {(queries ?? []).map((q: any) => (
                <div key={q.id} className="rounded-lg border border-divider/60 p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium leading-snug">{q.question}</p>
                    {q.status === "answered" ? (
                      <Badge className="shrink-0 bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-xs">
                        answered
                      </Badge>
                    ) : (
                      <Badge className="shrink-0 bg-amber-500/15 text-amber-400 border-amber-500/20 text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3" /> pending
                      </Badge>
                    )}
                  </div>
                  {q.answer && (
                    <p className="text-sm text-muted-foreground border-t border-divider/60 pt-2 leading-relaxed">
                      {q.answer}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground/50 font-mono">
                    {new Date(q.createdAt).toLocaleString()}
                    {q.answeredAt && ` · answered ${new Date(q.answeredAt).toLocaleString()}`}
                  </p>
                </div>
              ))}
              {(queries ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No queries yet</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Run history */}
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground">
            Run history
          </CardTitle>
        </CardHeader>
        <CardContent>
          {runsLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (runs ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No runs yet</p>
          ) : (
            <div className="space-y-2">
              {(runs ?? []).slice(0, 20).map((run: any) => (
                <div
                  key={run.id}
                  className="flex items-start gap-3 rounded-lg border border-divider/60 px-3 py-2.5 hover:bg-surface-subtle transition-colors"
                >
                  <div className="mt-0.5">
                    {run.status === "succeeded" ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    ) : run.status === "failed" ? (
                      <XCircle className="h-4 w-4 text-red-400" />
                    ) : (
                      <Clock className="h-4 w-4 text-amber-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={run.status} />
                      <span className="text-xs font-mono text-muted-foreground">{run.channel}</span>
                      {Array.isArray(run.actions) && run.actions.length > 0 && (
                        <span className="text-xs text-blue-400 font-mono">
                          {run.actions.length} action{run.actions.length !== 1 ? "s" : ""}
                        </span>
                      )}
                      {run.costUsd > 0 && (
                        <span className="text-xs text-amber-400 font-mono">${run.costUsd.toFixed(4)}</span>
                      )}
                    </div>
                    {run.summary && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{run.summary}</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground/50 font-mono shrink-0 mt-0.5">
                    {new Date(run.completedAt).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
